import { supabase } from "../supabase";

export interface Tag {
  id: string;
  name: string;
  /** 並び順。null は「まだ並べ替えていない」で、名前順の末尾に回す */
  sortOrder: number | null;
}

/** タグ名の上限。チップやメニューに収まる長さに抑える */
export const TAG_NAME_MAX = 20;

/** 前後の空白を落とし、空・上限超えは無効とみなす(入力欄側は maxLength で先に止まる) */
export function normalizeTagName(raw: string): string | null {
  const name = raw.trim();
  return name === "" || name.length > TAG_NAME_MAX ? null : name;
}

interface TagRow {
  id: string;
  name: string;
  sort_order?: number | null;
}

/** 並べ替え済みが先、未設定は名前順で後ろ。一覧のチップも管理画面もこの順で出す */
export function sortTags<T extends { name: string; sortOrder: number | null }>(tags: T[]): T[] {
  return [...tags].sort((a, b) => {
    const orderA = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name, "ja");
  });
}

export async function fetchTags(): Promise<Tag[]> {
  // sort_order 列がまだ無い環境(マイグレーション適用前)でも一覧が壊れないよう、
  // 列を名指しせずに取り、並びはクライアント側で決める
  const { data, error } = await supabase.from("tags").select("*");
  if (error) throw error;
  const rows = data as TagRow[];
  return sortTags(
    rows.map((row) => ({ id: row.id, name: row.name, sortOrder: row.sort_order ?? null })),
  );
}

/**
 * 並び順を保存する。渡した並びの添字がそのまま順位になる。
 * 1回の upsert でまとめて書き、途中失敗で中途半端な並びが残らないようにする。
 * name も添えるのは、既存行との衝突(=更新)に必要な NOT NULL 列だから。
 */
export async function reorderTags(ordered: { id: string; name: string }[]): Promise<void> {
  const rows = ordered.map((tag, index) => ({ id: tag.id, name: tag.name, sort_order: index }));
  const { error } = await supabase.from("tags").upsert(rows);
  if (error) throw error;
}

/**
 * タグを作る。既にあれば作らずにそれを返す。
 * couple_id は DB 側の既定値で埋まる(→ 20260813000003_media_defaults.sql)。
 */
export async function createTag(name: string): Promise<Tag> {
  // 末尾に置くために、作る前の並びを控えておく
  const existing = await fetchTags();
  const { data, error } = await supabase
    .from("tags")
    .upsert({ name }, { onConflict: "couple_id,name" })
    .select("id, name, sort_order")
    .single();
  if (error) throw error;
  const row = data as TagRow;
  // 既にあったタグ(番号付き)はそのまま。作ったばかりのものだけ末尾へ送る
  const order = row.sort_order ?? null;
  if (order !== null) return { id: row.id, name: row.name, sortOrder: order };

  // 番号の無いタグは名前順で出るため、作ったものが列の途中に紛れてしまう。
  // 表示順のまま 0 からの連番を振り直し、新しいものを最後に置く
  const ordered = [
    ...existing.filter((tag) => tag.id !== row.id).map((tag) => ({ id: tag.id, name: tag.name })),
    { id: row.id, name: row.name },
  ];
  try {
    await reorderTags(ordered);
  } catch (e) {
    // 並びを直せなくてもタグ自体はできている。次の並べ替えで直せる
    console.error("並び順の振り直しに失敗", e);
  }
  return { id: row.id, name: row.name, sortOrder: ordered.length - 1 };
}

/** 名前を変える。同じ名前が既にあれば DB の一意制約で弾かれる */
export async function renameTag(id: string, name: string): Promise<void> {
  const { error } = await supabase.from("tags").update({ name }).eq("id", id);
  if (error) throw error;
}

/**
 * タグを消す。メディアは消えない。
 * `media_tags` は外部キーの連鎖削除で一緒に消える(→ 初期スキーマ)。
 */
export async function deleteTag(id: string): Promise<void> {
  const { error } = await supabase.from("tags").delete().eq("id", id);
  if (error) throw error;
}

/**
 * 選んだメディアそれぞれに何件付いているかを、タグごとに数える。
 * 一括付与の面で「全件に付いている / 一部だけ / どれにも付いていない」を
 * 出し分けるために使う。
 */
export async function countTagsOf(mediaIds: string[]): Promise<Map<string, number>> {
  const counts = new Map<string, number>();
  if (mediaIds.length === 0) return counts;
  const { data, error } = await supabase
    .from("media_tags")
    .select("tag_id")
    .in("media_id", mediaIds);
  if (error) throw error;
  const rows: { tag_id: string }[] = data;
  for (const row of rows) counts.set(row.tag_id, (counts.get(row.tag_id) ?? 0) + 1);
  return counts;
}

export async function attachTag(mediaIds: string[], tagId: string): Promise<void> {
  if (mediaIds.length === 0) return;
  // **ignoreDuplicates を外さないこと。** 既定の upsert は
  // `on conflict do update` になり、media_tags に無い UPDATE 権限を要求して
  // 「permission denied」で落ちる。付け直すものは何も無いので do nothing でよい
  const { error } = await supabase.from("media_tags").upsert(
    mediaIds.map((media_id) => ({ media_id, tag_id: tagId })),
    { onConflict: "media_id,tag_id", ignoreDuplicates: true },
  );
  if (error) throw error;
}

/** まとめて解除する。付いていないものが混ざっていても、消す行が無いだけで通る */
export async function detachTagFrom(mediaIds: string[], tagId: string): Promise<void> {
  if (mediaIds.length === 0) return;
  const { error } = await supabase
    .from("media_tags")
    .delete()
    .in("media_id", mediaIds)
    .eq("tag_id", tagId);
  if (error) throw error;
}

/**
 * メディアを削除する。
 *
 * 物理削除ではなく `deleted_at` を立てる。二人ともどのメディアでも削除できる仕様
 * (要件定義 3.5)のため、取り違えたときに戻せる余地を残しておく。
 */
export async function softDeleteMedia(mediaIds: string[]): Promise<void> {
  const { error } = await supabase
    .from("media")
    .update({ deleted_at: new Date().toISOString() })
    .in("id", mediaIds);
  if (error) throw error;
}
